import EventEmitter from "events";
import { Message } from "src/modules/communication-protobuf";
import createPeerConnection from "./createPeerConnection";
import debug from "src/modules/debug-logger";
import { getVideoConstraints } from "src/modules/rtc/media-constraints";
import { getVideoQuality } from "src/duck/selectors";
import { store } from "src/store/store";

const log = window.require("electron-log");

const SEND_CANDIDATES_TIMEOUT = 200;

export default class RtcManager extends EventEmitter {
  constructor({ messenger, isPolite }) {
    super();
    this.messenger = messenger;
    this.isPolite = isPolite;
    this.isWaitingForInitialOffer = isPolite;
    this.isMakingOffer = false;
    this.videoTrack = null;
    this.peers = {};
  }
  get hasCamera() {
    return this.videoTrack == null ? false : true;
  }
  getPeer = (jid) => this.peers[jid];
  updatePeer = (jid, update) => {
    let peer = this.getPeer(jid);
    if (peer == null) return;
    const { videoQualitySettings, networkType } = update;
    if (videoQualitySettings && networkType && peer.videoTrack && peer.videoTrack.readyState !== "ended") {
      const videoQuality = networkType === "WIFI" ? videoQualitySettings.onWifi : videoQualitySettings.onCellular;
      peer.videoTrack.applyConstraints(getVideoConstraints({ videoQuality }).constraints.video);
    }
    this.peers[jid] = { ...peer, ...update };
  };
  initializePeer(peerJid, peersState) {
    this.isWaitingForInitialOffer = this.isPolite;
    const peerConnection = this.initializePeerConnection(peerJid);

    const commDataChannel = this.initializeDataChannel({
      peerConnection,
      peerJid,
      label: "comm",
      id: 1
    });
    const audioDataChannel = this.initializeDataChannel({
      peerConnection,
      peerJid,
      label: "audio",
      id: 2
    });

    this.peers[peerJid] = {
      ...this.peers[peerJid],
      ...peersState,
      candidates: {
        toSend: [],
        toSendTimeoutId: null,
        toAdd: []
      },
      jid: peerJid,
      peerConnection,
      getPeerConnection: () => (this.peers[peerJid] ? this.peers[peerJid].peerConnection : null),
      commDataChannel,
      audioDataChannel
    };

    this.messenger.addPeer(this.peers[peerJid]);

    return this.peers[peerJid];
  }
  recreateConnection({ peerJid, delayVideoReconnect }) {
    this.destroyPeerConnection(peerJid);

    const newPeer = this.initializePeer(peerJid);
    const newPeerConnection = newPeer.peerConnection;

    if (newPeer.isInVideoMode) {
      const restartVideo = (peerJid) => {
        const peer = this.getPeer(peerJid);
        if (peer?.peerConnection !== newPeerConnection) return;
        if (
          peer.peerConnection.connectionState === "closed" ||
          peer.peerConnection.connectionState === "failed" ||
          peer.peerConnection.connectionState === "disconnected"
        ) {
          this.off("connection-state-change", restartVideo);
          return;
        }
        if (peer.peerConnection.connectionState === "connected" || peer.peerConnection.connectionState === undefined) {
          this.off("connection-state-change", restartVideo);
          if (peer.isInVideoMode) {
            debug.rtcManager("Restarting video");
            if (delayVideoReconnect) {
              setTimeout(() => peer.isInVideoMode && this.startSendingVideoTo(peerJid), 5000);
            } else this.startSendingVideoTo(peerJid);
          }
        }
      };
      this.on("connection-state-change", restartVideo);
    }

    return newPeer;
  }

  sendCandidates = (peerJid) => {
    const peer = this.peers[peerJid];
    if (peer == null) return;

    const { toSend } = peer.candidates;
    if (toSend.length === 0) return;
    this.peers[peerJid].candidates.toSend = [];
    const message = Message.WebRTCCandidate.create(toSend);
    this.messenger.sendAsEnvelope({
      to: peerJid,
      payload: { message },
      sendBy: "xmpp"
    });
  };
  initializePeerConnection(peerJid) {
    debug.rtcManager(`Initializing PeerConnection with peer: ${peerJid}`);

    const peerConnection = createPeerConnection();

    peerConnection.ontrack = (e) => {
      debug.rtcManager("ontrack fired", e);
      this.emit("media-track", e, peerJid);
    };
    peerConnection.onicecandidate = (e) => {
      // debug.signaling("onicecandidate fired:", e);
      const { candidate } = e;
      const peer = this.peers[peerJid];
      if (candidate && peer) {
        clearTimeout(peer.candidates.toSendTimeoutId);
        peer.candidates.toSend.push({
          sdp: candidate.candidate,
          sdpMLineIndex: candidate.sdpMLineIndex,
          sdpMid: candidate.sdpMid
        });
        peer.candidates.toSendTimeoutId = setTimeout(() => this.sendCandidates(peerJid), SEND_CANDIDATES_TIMEOUT);
      }
    };
    peerConnection.onnegotiationneeded = async (e) => {
      try {
        debug.signaling("onnegotiationneeded fired:", e);
        if (this.isWaitingForInitialOffer) {
          debug.rtcManager("Ignoring negotiation needed, waiting for initial offer");
          return;
        }
        this.isMakingOffer = true;

        await peerConnection.setLocalDescription();
        const message = Message.WebRTCCreateOffer.create(peerConnection.localDescription);
        this.messenger.sendAsEnvelope({ to: peerJid, payload: { message }, sendBy: "xmpp" });
      } catch (err) {
        console.log("err: ", err);
        debug.rtcManager("Sending local desc. failed: ", err);
      } finally {
        this.isMakingOffer = false;
      }
    };
    peerConnection.onsignalingstatechange = () => {
      debug.signaling(`signalingState: ${peerConnection.signalingState}`, peerJid);
      this.emit("signaling-state-change", peerJid);
    };
    peerConnection.onicegatheringstatechange = () => {
      debug.signaling(`iceGatheringState: ${peerConnection.iceGatheringState}`, peerJid);
      if (peerConnection.iceGatheringState === "complete") {
        clearTimeout(this.peers[peerJid].candidates.toSendTimeoutId);
        this.sendCandidates(peerJid);
      }
    };
    peerConnection.oniceconnectionstatechange = () => {
      debug.signaling(`iceConnectionState: ${peerConnection.iceConnectionState}`, peerJid);
      this.emit("ice-connection-state-change", peerJid);
      if (peerConnection.iceConnectionState === "failed") {
        peerConnection.restartIce();
        return;
      }
    };
    peerConnection.onconnectionstatechange = () => {
      debug.signaling(`connectionState: ${peerConnection.connectionState}`, peerJid);
      this.emit("connection-state-change", peerJid);
    };

    return peerConnection;
  }

  initializeDataChannel({ label, id, peerConnection, peerJid }) {
    const dataChannel = peerConnection.createDataChannel(label, {
      id,
      ordered: true,
      maxRetransmits: label === "audio" ? 2 : 5,
      negotiated: true
    });
    dataChannel.binaryType = "arraybuffer";
    debug.rtcManager(`initializing dataChannel ${dataChannel.label}, peerJid:`, peerJid);

    dataChannel.onopen = (e) => {
      debug.rtcManager("dataChannel.onopen fired", e, peerJid);
      this.emit("data-channel-opened", e, dataChannel, peerJid);
    };
    dataChannel.onclose = (e) => {
      peerConnection && peerConnection.close();
      debug.rtcManager("dataChannel.onclose fired", e, peerJid);
      this.emit("data-channel-closed", e, dataChannel, peerJid);
    };
    dataChannel.onerror = (e) => {
      peerConnection && peerConnection.close();
      debug.rtcManager("dataChannel.onerror fired", e, peerJid);
      this.emit("data-channel-error", e, dataChannel, peerJid);
    };
    return dataChannel;
  }

  async createAndSendAnswerTo(aPeer) {
    const { peerConnection, jid } = aPeer;

    if (peerConnection == null) return;
    debug.signaling("about to create answer:", jid);
    const answer = await peerConnection.createAnswer();

    if (peerConnection == null) return;
    try {
      await peerConnection.setLocalDescription(answer);
    } catch (err) {
      debug.signaling("Failed setting local description ", err);
    }

    if (peerConnection == null) return;
    debug.signaling("answer created, about to send it to:", jid);
    const message = Message.WebRTCCreateAnswer.create(peerConnection.localDescription);

    this.messenger.sendAsEnvelope({
      to: jid,
      payload: { message },
      sendBy: "xmpp"
    });
  }

  handleOffer = async ({ sdp }, from) => {
    let peer = this.getPeer(from);
    if (peer == null || peer.peerConnection == null) return;
    const { peerConnection } = peer;

    const offerCollision = this.isMakingOffer || peerConnection.signalingState !== "stable";
    const ignoreOffer = !this.isPolite && offerCollision;

    if (ignoreOffer) {
      debug.signaling("Ignoring offer", peerConnection.signalingState, this.isMakingOffer, this.isPolite);
      return;
    }

    await this.applyOffer({ sdp, peer });
    this.createAndSendAnswerTo(peer);
  };

  applyOffer = async ({ sdp, peer }) => {
    if (peer == null || peer.peerConnection == null) return;
    debug.signaling("about to apply offer, peerJid", peer.jid);
    const desc = new RTCSessionDescription({ sdp, type: "offer" });
    await peer.peerConnection.setRemoteDescription(desc);
    this.isWaitingForInitialOffer = false;
    debug.signaling(`offer applied`, peer.jid);
  };

  handleAnswer = async ({ sdp }, from) => {
    const peer = this.getPeer(from);
    if (peer == null || peer.peerConnection == null) return;

    if (peer.peerConnection.signalingState === "have-local-offer") {
      await this.applyAnswer({ sdp, peer });
    } else {
      debug.signaling("cannot applyAnswer, signalingState:", peer.peerConnection.signalingState, from);
    }
  };
  applyAnswer = async ({ sdp, peer }) => {
    debug.signaling("about to apply answer", peer.jid);
    try {
      const desc = new RTCSessionDescription({ sdp, type: "answer" });
      await peer.peerConnection.setRemoteDescription(desc);
      debug.signaling("answer applied", peer.jid);
    } catch (error) {
      debug.signaling("applyAnswer failed", error);
    }
  };

  handleWebRTCCandidates = async ({ candidates }, from) => {
    const peer = this.getPeer(from);
    if (peer && peer.peerConnection) {
      candidates.forEach((candidate) => {
        const { sdp, ...rest } = candidate;
        const parsedCandidate = { ...rest, candidate: sdp };
        this.applyIceCandidate({
          candidate: parsedCandidate,
          peer
        });
      });
    }
  };
  applyIceCandidate = async ({ candidate, peer }) => {
    if (peer == null || peer.peerConnection == null) return;
    try {
      debug.rtcManager("Adding ice candidate ", candidate);
      await peer.peerConnection.addIceCandidate(candidate);
    } catch (error) {
      log.error("Add ICE Candidate failed", error, peer.jid);
    }
  };

  setPeerVolume = (volume = 1, jid) => {
    const peer = this.getPeer(jid);
    if (peer && peer.opusPlayer) {
      peer.opusPlayer.volume(volume);
    }
  };

  async updatePeersVideoTrack(jid) {
    const peer = this.getPeer(jid);
    if (peer == null || peer.videoTrack == null || peer.videoTrack.readyState === "ended") return;
    let videoQuality = getVideoQuality(store.getState());

    const { networkType, videoQualitySettings } = peer;
    if (networkType && videoQualitySettings) {
      const { onWifi, onCellular } = videoQualitySettings;
      videoQuality = networkType === "WIFI" ? onWifi : onCellular;
    }

    await peer.videoTrack.applyConstraints(getVideoConstraints({ videoQuality }).constraints.video);
  }

  async addVideoTrackToPeer(jid) {
    const peer = this.getPeer(jid);
    if (peer == null) return;
    if (peer.videoTrack) peer.videoTrack.stop();
    peer.videoTrack = this.videoTrack.clone();
    await this.updatePeersVideoTrack(jid);
  }

  async changeCamera({ cameraId }) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        getVideoConstraints({
          videoOptions: cameraId ? { deviceId: cameraId } : undefined,
          videoQuality: "HIGH"
        }).constraints
      );
      this.videoTrack.stop();
      this.videoStream.removeTrack(this.videoTrack);
      this.videoTrack = stream.getVideoTracks()[0];
      this.videoStream.addTrack(this.videoTrack);

      await Promise.all(
        Object.values(this.peers).map(async (peer) => {
          if (peer.videoTrack) peer.videoTrack.stop();
          if (peer.videoTransceiver == null) return;
          await this.addVideoTrackToPeer(peer.jid);
          await peer.videoTransceiver.sender.replaceTrack(peer.videoTrack);
          // await this.createAndSendOfferTo(peer);
        })
      );
    } catch (err) {
      log.error(err);
    }
  }

  async startSendingVideoTo(jid, deviceId) {
    const peer = this.getPeer(jid);
    if (peer == null) return;
    try {
      if (this.videoTrack.readyState === "ended") {
        const stream = await navigator.mediaDevices.getUserMedia(
          getVideoConstraints({
            videoOptions: { deviceId },
            videoQuality: "HIGH"
          }).constraints
        );
        this.videoStream.removeTrack(this.videoTrack);
        this.videoTrack = stream.getVideoTracks()[0];
        this.videoStream.addTrack(this.videoTrack);
        await this.addVideoTrackToPeer(jid);
        await peer.videoTransceiver.sender.replaceTrack(peer.videoTrack);
      }
      if (peer.videoTransceiver == null) {
        await this.addVideoTrackToPeer(jid);
        peer.videoTransceiver = peer.peerConnection.addTransceiver(peer.videoTrack, {
          direction: "inactive",
          streams: [this.videoStream]
        });
      }
      peer.videoTransceiver.direction = "sendonly";
      peer.isInVideoMode = true;
    } catch (err) {
      log.error(err);
    }
  }

  async stopSendingVideoTo(jid) {
    const peer = this.getPeer(jid);
    if (peer && peer.videoTransceiver && peer.isInVideoMode) {
      peer.videoTransceiver.direction = "inactive";
      peer.isInVideoMode = false;
    }
  }

  destroyPeerConnection(peerJid) {
    const peer = this.peers[peerJid];
    if (peer == null || peer.peerConnection == null) return;

    if (peer.candidates.toSendTimeoutId) clearTimeout(peer.candidates.toSendTimeoutId);

    this.messenger.removePeer(peerJid);

    peer.peerConnection.ontrack = () => {};
    peer.peerConnection.onicecandidate = () => {};
    peer.peerConnection.onnegotiationneeded = () => {};
    peer.peerConnection.onsignalingstatechange = () => {};
    peer.peerConnection.onicegatheringstatechange = () => {};
    peer.peerConnection.oniceconnectionstatechange = () => {};
    peer.peerConnection.onconnectionstatechange = () => {};
    peer.peerConnection.ontrack = () => {};
    peer.commDataChannel.onopen = () => {};
    peer.commDataChannel.onclose = () => {};
    peer.commDataChannel.onerror = () => {};
    peer.audioDataChannel.onopen = () => {};
    peer.audioDataChannel.onclose = () => {};
    peer.audioDataChannel.onerror = () => {};

    peer.videoTrack && peer.videoTrack.stop();
    peer.videoTransceiver = null;
    peer.peerConnection = null;
    peer.commDataChannel = null;
    peer.audioDataChannel = null;
    delete peer.videoTransceiver;
    delete peer.peerConnection;
    delete peer.commDataChannel;
    delete peer.audioDataChannel;
  }

  _destroy() {
    debug.rtcManager("about to destroy itself");

    this.removeListeners();
    const peers = Object.values(this.peers);

    this.opusRecorder && this.opusRecorder.destroy();
    this.opusRecorder = null;

    peers.forEach((peer) => {
      peer.opusPlayer && peer.opusPlayer.destroy();
      peer.opusPlayer = null;

      this.messenger.removePeer(peer.jid);
      this.destroyPeerConnection(peer.jid);
      delete this.peers[peer.jid];
    });
    if (this.videoTrack) {
      this.videoTrack.stop();
      this.videoTrack.enabled = false;
    }

    this.ignoreOffer = false;
  }
}
